import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Calculator, Plus, Edit, Trash, RefreshCw, History, FunctionSquare, RotateCcw, Database, Save, ChevronDown, ChevronRight,
    LayoutGrid, FolderTree, Shapes, Binary,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { PAYROLL_VARIABLE_GROUPS } from '../payroll/core/catalog';
import { canDeclarePayrollConstant, canonicalPayrollCode, PAYROLL_VARIABLE_KIND_LABELS, resolveEffectiveDatedRow } from '../payroll/core/domain';
import { extractPayrollDependencies } from '../payroll/core/formulaEvaluator';
import {
    DATA_TYPE_LABELS, HE_THONG_SOURCE_OPTIONS, VARIABLE_SOURCE_LABELS, effectiveFormulaToday,
    findMissingSeedRows, normalizeVariableInput, validateFormulaConfiguration,
} from '../payroll/core/configRules';
import {
    addConstant, addFormulaVersion, deleteConstant, deleteFormulaVersion, deleteVariable,
    loadConfiguration, saveVariable, seedMissingCatalog, setVariableActive,
} from '../payroll/data/configActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage } from '../payroll/data/permissions';
import { parseSheetNumber } from '../payroll/data/sheetFormat';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, Field, FilterField, IconAction, ListToolbar, MessageList, Modal, NumberInput, PageShell, SectionTitle,
    SortableTh, Spinner, StatCards, buttonPrimary, buttonSecondary, formatDateTime, formatDay, fromInputDate, inputClass, matchesSearch,
    tableClass, tbodyClass, tdClass, thClass, theadClass, toInputDate, useSortedRows,
} from '../payroll/components/ui';

const KIND_TONES = { NHAP_TAY: 'yellow', HE_THONG: 'blue', THOA_THUAN: 'purple', CONG_THUC: 'green' };
const GROUPINGS = [
    { value: 'all', label: 'Tất cả', icon: LayoutGrid },
    { value: 'nhom', label: 'Nhóm biến', icon: FolderTree },
    { value: 'loai', label: 'Loại biến', icon: Shapes },
    { value: 'kieu', label: 'Kiểu dữ liệu', icon: Binary },
];
const firstDayOfMonth = () => { const today = new Date(); return new Date(today.getFullYear(), today.getMonth(), 1); };

const formatConstant = (value, dataType) => {
    if (value === null || value === undefined) return '';
    if (dataType === 'TY_LE') return `${value.toLocaleString('vi-VN')}%`;
    return value.toLocaleString('vi-VN');
};

const isConstantVariable = (variable) => canDeclarePayrollConstant(variable.LOAI_BIEN, variable.NGUON_DU_LIEU, variable.MA_BIEN);

function VariableForm({ initial, isNew, config, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [formulaFrom, setFormulaFrom] = useState(toInputDate(firstDayOfMonth()));
    const [saving, setSaving] = useState(false);
    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const { errors, variable } = normalizeVariableInput(form, { existingCodes: config.variables.map((item) => item.MA_BIEN), isNew });
    const formulaError = form.LOAI_BIEN === 'CONG_THUC' && variable.CONG_THUC && variable.MA_BIEN
        ? validateFormulaConfiguration(config.variables, { code: variable.MA_BIEN, formula: variable.CONG_THUC })
        : null;
    const formulaChanged = form.LOAI_BIEN === 'CONG_THUC' && (isNew || initial.CONG_THUC !== variable.CONG_THUC);
    const groups = Array.from(new Set([...PAYROLL_VARIABLE_GROUPS, ...config.variables.map((item) => item.NHOM).filter(Boolean)]));

    const handleSave = async () => {
        try {
            setSaving(true);
            await saveVariable(form, { isNew, config, formulaFrom: fromInputDate(formulaFrom) });
            toast.success(isNew ? 'Đã thêm biến lương' : 'Đã cập nhật biến lương');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={isNew ? 'Thêm biến lương' : 'Cập nhật biến lương'} subtitle={isNew ? 'Biến dùng làm cột của bảng lương hoặc trong công thức' : `Mã ${initial.MA_BIEN}`}
            size="max-w-3xl" onClose={onClose} disabled={saving}
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Hủy</button>
                    <button className={buttonPrimary} onClick={handleSave} disabled={saving || errors.length > 0 || Boolean(formulaError)}>
                        <Save className="w-4 h-4" />{saving ? 'Đang lưu...' : 'Lưu'}
                    </button>
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Mã biến" required hint="Dùng trong công thức, ví dụ PCK, DGN">
                    <input className={inputClass} value={form.MA_BIEN} disabled={!isNew} onChange={(e) => set('MA_BIEN', e.target.value.toUpperCase())} />
                </Field>
                <Field label="Tên biến" required>
                    <input className={inputClass} value={form.TEN_BIEN} onChange={(e) => set('TEN_BIEN', e.target.value)} />
                </Field>
                <Field label="Loại biến" required>
                    <select className={inputClass} value={form.LOAI_BIEN} onChange={(e) => set('LOAI_BIEN', e.target.value)}>
                        {Object.entries(PAYROLL_VARIABLE_KIND_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                </Field>
                {form.LOAI_BIEN === 'HE_THONG' ? (
                    <Field label="Nguồn dữ liệu" required hint="Biến hệ thống phải chọn nguồn, nếu không giá trị sẽ luôn bằng 0">
                        <select className={inputClass} value={form.NGUON_DU_LIEU} onChange={(e) => set('NGUON_DU_LIEU', e.target.value)}>
                            <option value="">Chọn nguồn dữ liệu</option>
                            {HE_THONG_SOURCE_OPTIONS.map((value) => <option key={value} value={value}>{VARIABLE_SOURCE_LABELS[value]}</option>)}
                        </select>
                    </Field>
                ) : <div />}
                <Field label="Kiểu dữ liệu">
                    <select className={inputClass} value={form.LOAI_DU_LIEU} onChange={(e) => set('LOAI_DU_LIEU', e.target.value)}>
                        {Object.entries(DATA_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                </Field>
                <Field label="Đơn vị" hint="Chỉ để hiển thị, ví dụ Ngày, Giờ">
                    <input className={inputClass} value={form.DON_VI} onChange={(e) => set('DON_VI', e.target.value)} />
                </Field>
                <Field label="Nhóm biến" hint="Chọn nhóm có sẵn hoặc gõ tên nhóm mới">
                    <input className={inputClass} list="payroll-variable-groups" value={form.NHOM} onChange={(e) => set('NHOM', e.target.value)} />
                    <datalist id="payroll-variable-groups">{groups.map((group) => <option key={group} value={group} />)}</datalist>
                </Field>
                <Field label="Thứ tự">
                    <input type="number" className={inputClass} value={form.THU_TU} onChange={(e) => set('THU_TU', e.target.value)} />
                </Field>
                {form.LOAI_BIEN === 'CONG_THUC' && (
                    <div className="md:col-span-2 space-y-3">
                        <Field label="Công thức" required hint="Dùng mã biến và các hàm IF, IFS, ROUND, MIN, MAX, SUM... Ví dụ: ROUND(MLT / NCC * NCT, 0)">
                            <textarea rows={3} className={`${inputClass} font-mono`} value={form.CONG_THUC} onChange={(e) => set('CONG_THUC', e.target.value.toUpperCase())} />
                        </Field>
                        {formulaError && <MessageList items={[formulaError]} />}
                        {formulaChanged && (
                            <Field label="Công thức áp dụng từ ngày" required hint="Kỳ lương trước ngày này vẫn tính bằng công thức cũ">
                                <input type="date" className={inputClass} value={formulaFrom} onChange={(e) => setFormulaFrom(e.target.value)} />
                            </Field>
                        )}
                    </div>
                )}
                <div className="md:col-span-2">
                    <Field label="Ghi chú">
                        <textarea rows={2} className={inputClass} value={form.MO_TA} onChange={(e) => set('MO_TA', e.target.value)} />
                    </Field>
                </div>
            </div>
            {errors.length > 0 && <div className="mt-4"><MessageList items={errors} /></div>}
        </Modal>
    );
}

function HistoryModal({ variable, type, config, canManage, onClose, onChanged }) {
    const isConstant = type === 'constants';
    const rows = (isConstant ? config.constants : config.formulas)
        .filter((row) => row.MA_BIEN === variable.MA_BIEN)
        .sort((left, right) => (right.HIEU_LUC_TU?.getTime() ?? 0) - (left.HIEU_LUC_TU?.getTime() ?? 0) || (right.UPDATED_AT?.getTime() ?? 0) - (left.UPDATED_AT?.getTime() ?? 0));
    const active = resolveEffectiveDatedRow(rows.filter((row) => row.HIEU_LUC_TU).map((row) => ({ ...row, UPDATED_AT: row.UPDATED_AT ?? new Date(0) })), new Date());
    const [form, setForm] = useState({ value: '', from: toInputDate(firstDayOfMonth()), to: '', note: '' });
    const [saving, setSaving] = useState(false);
    const [deleteRow, setDeleteRow] = useState(null);
    const canAdd = canManage && (isConstant ? isConstantVariable(variable) : variable.LOAI_BIEN === 'CONG_THUC');
    const parsedValue = isConstant ? parseSheetNumber(form.value, null) : null;

    const handleAdd = async () => {
        try {
            setSaving(true);
            const payload = { HIEU_LUC_TU: fromInputDate(form.from), HIEU_LUC_DEN: fromInputDate(form.to), GHI_CHU: form.note };
            if (isConstant) await addConstant(variable, { ...payload, GIA_TRI: parsedValue });
            else await addFormulaVersion(variable, { ...payload, CONG_THUC: form.value }, config);
            toast.success(isConstant ? 'Đã thêm mức giá trị mới' : 'Đã thêm đời công thức mới');
            setForm({ value: '', from: form.from, to: '', note: '' });
            await onChanged();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={isConstant ? 'Lịch sử giá trị' : 'Lịch sử công thức'} subtitle={`${variable.TEN_BIEN} (${variable.MA_BIEN})`} icon={History} onClose={onClose} disabled={saving} size="max-w-4xl"
            footer={<button className={buttonSecondary} onClick={onClose}>Đóng</button>}>
            <p className="text-sm text-gray-600 mb-4">
                {isConstant
                    ? 'Mỗi lần đổi mức thì thêm một dòng mới với ngày áp dụng; mức cũ giữ lại để kỳ lương cũ vẫn tính đúng. Nếu hai dòng cùng phủ một ngày, dòng thêm sau được dùng.'
                    : 'Mỗi lần đổi công thức thì thêm một đời mới với ngày áp dụng; kỳ lương lấy đời đang hiệu lực tại ngày cuối kỳ.'}
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg mb-5">
                <table className={tableClass}>
                    <thead className={theadClass}>
                        <tr>
                            <th className={thClass}>{isConstant ? 'Giá trị' : 'Công thức'}</th>
                            <th className={thClass}>Từ ngày</th>
                            <th className={thClass}>Đến ngày</th>
                            <th className={thClass}>Ghi chú</th>
                            <th className={thClass}>Người tạo / cập nhật</th>
                            <th className={thClass} />
                        </tr>
                    </thead>
                    <tbody className={tbodyClass}>
                        {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-sm text-gray-400">Chưa có bản ghi lịch sử nào được lưu cho biến này.</td></tr>}
                        {rows.map((row) => (
                            <tr key={row.ID} className={row.ID === active?.ID ? 'bg-green-50' : ''}>
                                <td className={`${tdClass} font-medium`}>
                                    {isConstant ? formatConstant(row.GIA_TRI, variable.LOAI_DU_LIEU) : <code className="font-mono text-xs">{row.CONG_THUC}</code>}
                                    {row.ID === active?.ID && <span className="ml-2"><Badge tone="green">Đang áp dụng</Badge></span>}
                                </td>
                                <td className={`${tdClass} whitespace-nowrap`}>{formatDay(row.HIEU_LUC_TU)}</td>
                                <td className={`${tdClass} whitespace-nowrap`}>{formatDay(row.HIEU_LUC_DEN) || 'Không thời hạn'}</td>
                                <td className={tdClass}>{row.GHI_CHU}</td>
                                <td className={`${tdClass} text-xs`}>{row.NGUOI_TAO} · {formatDateTime(row.UPDATED_AT)}</td>
                                <td className={tdClass}>
                                    {canManage && <IconAction icon={Trash} title={isConstant ? 'Xóa mức này' : 'Xóa đời công thức này'} tone="red" onClick={() => setDeleteRow(row)} disabled={saving} />}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {canAdd && (
                <div className="bg-gray-50 border rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">{isConstant ? 'Thêm mức mới' : 'Thêm đời công thức mới'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className={isConstant ? '' : 'md:col-span-4'}>
                            <Field label={`${isConstant ? 'Giá trị' : 'Công thức'}${variable.LOAI_DU_LIEU === 'TY_LE' && isConstant ? ' (%)' : ''}`} required
                                hint={isConstant ? 'Số lẻ gõ dấu phẩy, ví dụ 1,5' : undefined}>
                                {isConstant
                                    ? <NumberInput value={form.value} onChange={(next) => setForm({ ...form, value: next })} />
                                    : <input className={`${inputClass} font-mono`} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value.toUpperCase() })} />}
                            </Field>
                        </div>
                        <Field label="Từ ngày" required>
                            <input type="date" className={inputClass} value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
                        </Field>
                        <Field label="Đến ngày" hint="Bỏ trống = không thời hạn">
                            <input type="date" className={inputClass} value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
                        </Field>
                        <Field label="Ghi chú">
                            <input className={inputClass} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                        </Field>
                    </div>
                    {isConstant && form.value && Number.isNaN(parsedValue) && <div className="mt-3"><MessageList items={['Giá trị phải là số.']} /></div>}
                    <div className="flex justify-end mt-4">
                        <button className={buttonPrimary} onClick={handleAdd} disabled={saving || !form.value || !form.from || (isConstant && Number.isNaN(parsedValue))}>
                            <Plus className="w-4 h-4" />Thêm
                        </button>
                    </div>
                </div>
            )}
            {deleteRow && (
                <ConfirmDialog title={isConstant ? 'Xác nhận xóa mức giá trị' : 'Xác nhận xóa đời công thức'} confirmLabel="Xóa" onClose={() => setDeleteRow(null)}
                    message={isConstant ? `Xóa mức ${formatConstant(deleteRow.GIA_TRI, variable.LOAI_DU_LIEU)} từ ${formatDay(deleteRow.HIEU_LUC_TU)}?` : `Xóa đời công thức từ ${formatDay(deleteRow.HIEU_LUC_TU)}?`}
                    onConfirm={async () => {
                        try {
                            if (isConstant) await deleteConstant(deleteRow);
                            else await deleteFormulaVersion(variable, deleteRow, config);
                            toast.success('Đã xóa');
                            setDeleteRow(null);
                            await onChanged();
                        } catch (error) {
                            toast.error(error.message);
                        }
                    }} />
            )}
        </Modal>
    );
}

function VariableDetail({ variable, config, currentValue, currentFormula, canManage, onClose, onEdit, onHistory }) {
    const code = canonicalPayrollCode(variable.MA_BIEN);
    const nameOf = (item) => config.variables.find((other) => canonicalPayrollCode(other.MA_BIEN) === item)?.TEN_BIEN || item;
    const dependencies = currentFormula ? extractPayrollDependencies(currentFormula) : [];
    const usedBy = config.variables.filter((other) => other.CONG_THUC && extractPayrollDependencies(other.CONG_THUC).includes(code)).map((other) => other.MA_BIEN);
    const templateUsage = config.templateColumns.filter((column) => canonicalPayrollCode(column.BIEN_ID) === code);
    const templateName = (id) => config.templates.find((template) => template.MA_MAU === id)?.TEN_MAU || id;
    const chips = (items) => (items.length ? <div className="flex flex-wrap gap-1">{items.map((item) => <Badge key={item} tone="indigo" title={nameOf(item)}>{item}</Badge>)}</div> : null);
    return (
        <Modal title={variable.TEN_BIEN} subtitle={`Mã ${variable.MA_BIEN}`} size="max-w-3xl" onClose={onClose}
            footer={(
                <>
                    {(isConstantVariable(variable) || variable.LOAI_BIEN === 'CONG_THUC') && (
                        <button className={buttonSecondary} onClick={onHistory}><History className="w-4 h-4" />Lịch sử</button>
                    )}
                    {canManage && <button className={buttonSecondary} onClick={onEdit}><Edit className="w-4 h-4" />Sửa</button>}
                    <button className={buttonSecondary} onClick={onClose}>Đóng</button>
                </>
            )}>
            <div className="flex flex-wrap gap-2 mb-4">
                <Badge tone={KIND_TONES[variable.LOAI_BIEN]}>{PAYROLL_VARIABLE_KIND_LABELS[variable.LOAI_BIEN]}</Badge>
                {variable.LOAI_BIEN === 'HE_THONG' && <Badge tone="blue">{VARIABLE_SOURCE_LABELS[variable.NGUON_DU_LIEU]}</Badge>}
                <Badge>{DATA_TYPE_LABELS[variable.LOAI_DU_LIEU]}{variable.DON_VI ? ` · ${variable.DON_VI}` : ''}</Badge>
                {variable.NHOM && <Badge>{variable.NHOM}</Badge>}
                {!variable.HOAT_DONG && <Badge tone="red">Ngừng dùng</Badge>}
            </div>
            <div className="space-y-4 text-sm">
                {variable.LOAI_BIEN === 'CONG_THUC' && (
                    <div><SectionTitle>Công thức đang áp dụng</SectionTitle><code className="font-mono text-green-800 break-all">{currentFormula}</code></div>
                )}
                {isConstantVariable(variable) && (
                    <div><SectionTitle>Giá trị đang áp dụng</SectionTitle>
                        {currentValue ? <p><b>{formatConstant(currentValue.GIA_TRI, variable.LOAI_DU_LIEU)}</b> từ {formatDay(currentValue.HIEU_LUC_TU)}{currentValue.HIEU_LUC_DEN ? ` đến ${formatDay(currentValue.HIEU_LUC_DEN)}` : ''}</p> : <p className="text-red-600">Chưa khai mức nào.</p>}
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><SectionTitle>Phụ thuộc vào</SectionTitle>{chips(dependencies) || <p className="text-gray-500">Không phụ thuộc biến nào.</p>}</div>
                    <div><SectionTitle>Được dùng bởi</SectionTitle>{chips(usedBy) || <p className="text-gray-500">Chưa công thức nào dùng biến này.</p>}</div>
                </div>
                <div><SectionTitle>Mẫu bảng lương đang dùng</SectionTitle>
                    {templateUsage.length ? (
                        <div className="flex flex-wrap gap-1">{templateUsage.map((column) => (
                            <Badge key={column.ID} tone="green" title={column.CONG_THUC ? `Mẫu này tự tính: ${column.CONG_THUC}` : undefined}>{templateName(column.MAU_ID)}{column.CONG_THUC ? ' *' : ''}</Badge>
                        ))}</div>
                    ) : <p className="text-gray-500">Chưa mẫu nào dùng biến này.</p>}
                </div>
                <div><SectionTitle>Ghi chú</SectionTitle><p className="text-gray-700">{variable.MO_TA || <span className="text-gray-500">Chưa có ghi chú.</span>}</p></div>
            </div>
        </Modal>
    );
}

const EMPTY_VARIABLE = { MA_BIEN: '', TEN_BIEN: '', LOAI_BIEN: 'NHAP_TAY', NGUON_DU_LIEU: '', LOAI_DU_LIEU: 'TIEN', DON_VI: '', CONG_THUC: '', NHOM: '', THU_TU: 0, MO_TA: '', HOAT_DONG: true };

const CauHinhCongLuong = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('ACTIVE');
    const [filterKind, setFilterKind] = useState('ALL');
    const [groupBy, setGroupBy] = useState('nhom');
    const [collapsed, setCollapsed] = useState(new Set());
    const [variableForm, setVariableForm] = useState(null);
    const [detailCode, setDetailCode] = useState(null);
    const [history, setHistory] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmSeed, setConfirmSeed] = useState(false);
    const canView = canViewPayrollPage(PAYROLL_PATHS.configuration);
    const canManage = canManagePayroll();

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setConfig(await loadConfiguration());
        } catch (error) {
            console.error('Lỗi tải cấu hình công lương:', error);
            toast.error('Không tải được cấu hình công lương');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const missing = useMemo(() => (config ? findMissingSeedRows(config) : null), [config]);
    const missingCount = missing ? Object.values(missing).reduce((sum, rows) => sum + rows.length, 0) : 0;

    const currentConstant = useCallback((variable) => {
        const rows = (config?.constants ?? []).filter((row) => row.MA_BIEN === variable.MA_BIEN && row.HIEU_LUC_TU)
            .map((row) => ({ ...row, UPDATED_AT: row.UPDATED_AT ?? new Date(0) }));
        return resolveEffectiveDatedRow(rows, new Date());
    }, [config]);
    const currentFormula = useCallback((variable) => effectiveFormulaToday((config?.formulas ?? []).filter((row) => row.MA_BIEN === variable.MA_BIEN))?.CONG_THUC || variable.CONG_THUC, [config]);

    const filtered = useMemo(() => (config?.variables ?? []).filter((variable) => (filter === 'ALL' || (filter === 'ACTIVE' ? variable.HOAT_DONG : filter === 'INACTIVE' ? !variable.HOAT_DONG : variable.LOAI_BIEN === 'CONG_THUC' && variable.HOAT_DONG))
        && (filterKind === 'ALL' || variable.LOAI_BIEN === filterKind)
        && matchesSearch(`${variable.MA_BIEN} ${variable.TEN_BIEN} ${variable.CONG_THUC} ${variable.MO_TA}`, search)), [config, filter, filterKind, search]);
    const accessors = useMemo(() => ({
        code: (row) => row.MA_BIEN, name: (row) => row.TEN_BIEN, group: (row) => row.NHOM, kind: (row) => row.LOAI_BIEN, dataKind: (row) => row.LOAI_DU_LIEU,
        order: (row) => row.THU_TU ?? 0,
    }), []);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors, { key: 'order', direction: 'asc' });
    const groups = useMemo(() => {
        if (groupBy === 'all') return [{ key: 'all', label: '', rows: sorted }];
        const keyOf = (row) => (groupBy === 'nhom' ? row.NHOM || 'Khác' : groupBy === 'loai' ? PAYROLL_VARIABLE_KIND_LABELS[row.LOAI_BIEN] : DATA_TYPE_LABELS[row.LOAI_DU_LIEU]);
        const map = new Map();
        for (const row of sorted) map.set(keyOf(row), [...(map.get(keyOf(row)) ?? []), row]);
        const order = groupBy === 'nhom' ? [...PAYROLL_VARIABLE_GROUPS, ...map.keys()] : [...map.keys()];
        return [...new Set(order)].filter((key) => map.has(key)).map((key) => ({ key, label: key, rows: map.get(key) }));
    }, [sorted, groupBy]);

    if (!canView) return <AccessDenied />;

    const variables = config?.variables ?? [];
    const detail = detailCode ? variables.find((item) => item.MA_BIEN === detailCode) : null;
    const runAction = async (action, successMessage) => {
        try {
            setBusy(true);
            const message = await action();
            toast.success(typeof message === 'string' ? message : successMessage);
            await reload();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setBusy(false);
        }
    };
    const toggleGroup = (key) => setCollapsed((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
    const openEdit = (variable) => setVariableForm({ initial: { ...EMPTY_VARIABLE, ...variable, CONG_THUC: variable.CONG_THUC || '' }, isNew: false });

    return (
        <PageShell icon={Calculator} title="Cấu hình công lương" subtitle="Quản lý nhóm biến, mã ngắn, hằng số công ty và công thức dùng trong bảng lương"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading || busy}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    {canManage && <button className={buttonPrimary} onClick={() => setVariableForm({ initial: EMPTY_VARIABLE, isNew: true })} disabled={!config}><Plus className="w-4 h-4" />Thêm biến</button>}
                </>
            )}>
            {!canManage && <div className="mb-4"><MessageList tone="blue" items={['Bạn đang ở chế độ chỉ xem. Chỉ Admin hoặc Quản lý mới sửa được cấu hình lương.']} /></div>}
            {canManage && missingCount > 0 && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <p className="text-sm text-yellow-800">
                        Danh mục còn thiếu {missing.variables.length} biến, {missing.constants.length} hằng số, {missing.formulas.length} công thức, {missing.templates.length} mẫu so với danh mục mặc định.
                    </p>
                    <button className={buttonSecondary} onClick={() => setConfirmSeed(true)} disabled={busy}><Database className="w-4 h-4" />Khởi tạo phần còn thiếu</button>
                </div>
            )}
            <StatCards active={filter} onSelect={setFilter} cards={[
                { key: 'groups', label: 'Nhóm biến', value: new Set(variables.map((item) => item.NHOM || 'Khác')).size, tone: 'purple', caption: 'Phân loại theo nghiệp vụ' },
                { key: 'active', label: 'Biến đang dùng', value: variables.filter((item) => item.HOAT_DONG).length, tone: 'indigo', caption: `${variables.filter((item) => item.LOAI_BIEN === 'CONG_THUC' && item.HOAT_DONG).length} biến công thức`, filterValue: 'ACTIVE' },
                { key: 'formula', label: 'Biến công thức', value: variables.filter((item) => item.LOAI_BIEN === 'CONG_THUC' && item.HOAT_DONG).length, tone: 'green', caption: 'Có lịch sử theo ngày hiệu lực', filterValue: 'FORMULA' },
                { key: 'inactive', label: 'Ngừng dùng', value: variables.filter((item) => !item.HOAT_DONG).length, tone: 'red', filterValue: 'INACTIVE' },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm mã, tên, công thức hoặc ghi chú..."
                filterCount={[filterKind !== 'ALL', filter !== 'ACTIVE'].filter(Boolean).length}
                onClearFilters={() => { setFilterKind('ALL'); setFilter('ACTIVE'); setSearch(''); }}
                extra={(
                    <div className="flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                        {GROUPINGS.map(({ value, label, icon: Icon }) => (
                            <button key={value} type="button" onClick={() => setGroupBy(value)} title={`Gom theo ${label.toLowerCase()}`}
                                className={`px-3 py-2 text-sm flex items-center gap-1.5 border-r border-gray-300 last:border-r-0 ${groupBy === value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                <Icon className="w-4 h-4" /><span className="hidden lg:inline">{label}</span>
                            </button>
                        ))}
                    </div>
                )}
                filters={(
                    <>
                        <FilterField label="Loại biến">
                            <select className={inputClass} value={filterKind} onChange={(e) => setFilterKind(e.target.value)}>
                                <option value="ALL">Tất cả loại biến</option>
                                {Object.entries(PAYROLL_VARIABLE_KIND_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Trạng thái">
                            <select className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)}>
                                <option value="ACTIVE">Đang dùng</option><option value="FORMULA">Biến công thức</option><option value="INACTIVE">Ngừng dùng</option><option value="ALL">Tất cả</option>
                            </select>
                        </FilterField>
                    </>
                )} />
            {loading || !config ? <Spinner /> : sorted.length === 0 ? (
                <EmptyState icon={Calculator} title="Không có biến nào phù hợp">Đổi bộ lọc hoặc thêm biến mới.</EmptyState>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className={tableClass}>
                        <thead className={theadClass}>
                            <tr>
                                <th className={`${thClass} text-center`}>STT</th>
                                <SortableTh label="Mã biến" sortKey="code" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Tên biến" sortKey="name" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Nhóm biến" sortKey="group" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Loại biến" sortKey="kind" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Kiểu dữ liệu" sortKey="dataKind" sort={sort} onSort={toggleSort} />
                                <th className={`${thClass} text-right`}>Giá trị</th>
                                <th className={thClass}>Công thức</th>
                                <th className={`${thClass} text-center`}>Thao tác</th>
                            </tr>
                        </thead>
                        {groups.map((group) => {
                            const isCollapsed = groupBy !== 'all' && collapsed.has(group.key);
                            return (
                                <tbody key={group.key} className={tbodyClass}>
                                    {groupBy !== 'all' && (
                                        <tr className="bg-indigo-50/60">
                                            <td colSpan={9} className="px-3 py-2">
                                                <button type="button" onClick={() => toggleGroup(group.key)} className="w-full flex items-center justify-between text-sm font-semibold text-indigo-900">
                                                    <span className="flex items-center gap-1">{isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}{group.label}</span>
                                                    <span className="text-xs font-normal text-indigo-700">{group.rows.length} biến</span>
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                    {!isCollapsed && group.rows.map((variable, index) => {
                                        const constant = isConstantVariable(variable) ? currentConstant(variable) : null;
                                        return (
                                            <tr key={variable.MA_BIEN} onClick={() => setDetailCode(variable.MA_BIEN)} className={`hover:bg-gray-50 transition-colors cursor-pointer ${variable.HOAT_DONG ? '' : 'opacity-60'}`}>
                                                <td className={`${tdClass} text-center text-gray-400`}>{index + 1}</td>
                                                <td className={`${tdClass} font-mono font-medium text-indigo-600 whitespace-nowrap`}>{variable.MA_BIEN}</td>
                                                <td className={`${tdClass} text-gray-900`}>{variable.TEN_BIEN}{!variable.HOAT_DONG && <span className="ml-2"><Badge>Ngừng dùng</Badge></span>}</td>
                                                <td className={tdClass}>{variable.NHOM || '—'}</td>
                                                <td className={tdClass}>
                                                    <Badge tone={KIND_TONES[variable.LOAI_BIEN]}>{PAYROLL_VARIABLE_KIND_LABELS[variable.LOAI_BIEN] || variable.LOAI_BIEN}</Badge>
                                                    {variable.LOAI_BIEN === 'HE_THONG' && <div className="text-xs text-gray-500 mt-1">{VARIABLE_SOURCE_LABELS[variable.NGUON_DU_LIEU]}</div>}
                                                </td>
                                                <td className={`${tdClass} whitespace-nowrap`}>{DATA_TYPE_LABELS[variable.LOAI_DU_LIEU]}{variable.DON_VI ? ` · ${variable.DON_VI}` : ''}</td>
                                                <td className={`${tdClass} text-right whitespace-nowrap font-semibold text-gray-900`}>
                                                    {constant ? formatConstant(constant.GIA_TRI, variable.LOAI_DU_LIEU) : isConstantVariable(variable) && variable.LOAI_BIEN === 'HE_THONG' ? <span className="text-red-600 font-normal">Chưa khai</span> : '—'}
                                                </td>
                                                <td className={`${tdClass} max-w-xs`}>{variable.LOAI_BIEN === 'CONG_THUC' ? <code className="font-mono text-xs text-green-800 break-all">{currentFormula(variable)}</code> : '—'}</td>
                                                <td className={tdClass} onClick={(event) => event.stopPropagation()}>
                                                    <div className="flex justify-center gap-1">
                                                        {isConstantVariable(variable) && <IconAction icon={History} title="Lịch sử giá trị" tone="blue" onClick={() => setHistory({ code: variable.MA_BIEN, type: 'constants' })} />}
                                                        {variable.LOAI_BIEN === 'CONG_THUC' && <IconAction icon={FunctionSquare} title="Lịch sử công thức" tone="green" onClick={() => setHistory({ code: variable.MA_BIEN, type: 'formulas' })} />}
                                                        {canManage && (
                                                            <>
                                                                <IconAction icon={Edit} title="Sửa" onClick={() => openEdit(variable)} disabled={busy} />
                                                                {variable.HOAT_DONG
                                                                    ? <IconAction icon={Trash} title="Xóa / ngừng dùng" tone="red" onClick={() => setDeleteTarget(variable)} disabled={busy} />
                                                                    : <IconAction icon={RotateCcw} title="Dùng lại" tone="green" disabled={busy} onClick={() => runAction(() => setVariableActive(variable.MA_BIEN, true), `Đã dùng lại biến ${variable.MA_BIEN}`)} />}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            );
                        })}
                    </table>
                </div>
            )}
            {detail && (
                <VariableDetail variable={detail} config={config} currentValue={currentConstant(detail)} currentFormula={currentFormula(detail)} canManage={canManage}
                    onClose={() => setDetailCode(null)}
                    onEdit={() => { setDetailCode(null); openEdit(detail); }}
                    onHistory={() => { setDetailCode(null); setHistory({ code: detail.MA_BIEN, type: detail.LOAI_BIEN === 'CONG_THUC' ? 'formulas' : 'constants' }); }} />
            )}
            {variableForm && <VariableForm {...variableForm} config={config} onClose={() => setVariableForm(null)} onSaved={() => { setVariableForm(null); reload(); }} />}
            {history && variables.find((item) => item.MA_BIEN === history.code) && (
                <HistoryModal variable={variables.find((item) => item.MA_BIEN === history.code)} type={history.type} config={config} canManage={canManage}
                    onClose={() => setHistory(null)} onChanged={async () => setConfig(await loadConfiguration())} />
            )}
            {deleteTarget && (
                <ConfirmDialog title="Xác nhận xóa biến lương" confirmLabel="Xóa" onClose={() => setDeleteTarget(null)}
                    message={<>Xóa biến <b>{deleteTarget.MA_BIEN}</b> — {deleteTarget.TEN_BIEN}? Nếu biến đang được dùng ở mẫu, công thức hoặc thỏa thuận, hệ thống sẽ chuyển sang ngừng dùng thay vì xóa.</>}
                    onConfirm={async () => { await runAction(() => deleteVariable(deleteTarget.MA_BIEN, config)); setDeleteTarget(null); }} />
            )}
            {confirmSeed && (
                <ConfirmDialog title="Khởi tạo danh mục mặc định" danger={false} confirmLabel="Khởi tạo" onClose={() => setConfirmSeed(false)}
                    message={`Thêm ${missingCount} dòng còn thiếu của danh mục mặc định? Những gì đã có sẽ giữ nguyên.`}
                    onConfirm={async () => { await runAction(() => seedMissingCatalog(config), 'Đã khởi tạo danh mục'); setConfirmSeed(false); }} />
            )}
        </PageShell>
    );
};

export default CauHinhCongLuong;
